import {
  getPhoneNumberIndex,
  getSimulatorMetadataString,
} from "./simulator-metadata-semantics"
import type { ChatWebSocketSimulatorConfigData } from "./simulators/chat-websocket-config"
import type { WebSocketSimulatorConfigData } from "./simulators/websocket-config"

interface OutboundCallConfig {
  trigger_call_endpoint: string
  trigger_call_headers: string
  trigger_call_payload: string
  phone_number_key?: string
}

interface VoicePreviewMetadata {
  phoneNumber: string
  phoneNumberIndex?: number
  audioCodec?: string
  customSipHeaders: string
  mediaEncryption?: string
}

interface PipeCatPreviewMetadata {
  apiKey: string
  agentName: string
  customData: string
}

interface LiveKitPreviewMetadata {
  tokenEndpoint: string
  livekitUrl: string
  agentName: string
  /**
   * The user's last dispatch-mode selection (see useDispatchMode). Stored
   * under metadata.livekit_dispatch_mode purely so it's visible to
   * validateAgentCreate — it is stripped before a save reaches the backend
   * (see getLiveKitMetadataForSave), since dispatch mode otherwise stays
   * fully derivable from livekit_agent_name.
   */
  dispatchMode?: "automatic" | "explicit"
}

interface SttPreviewMetadata {
  provider: string
  model: string
  language: string
}

interface AgentPreviewMetadataView {
  voice: VoicePreviewMetadata
  outboundCall: OutboundCallConfig
  pipecat: PipeCatPreviewMetadata
  livekit: LiveKitPreviewMetadata
  stt: SttPreviewMetadata
  websocket: WebSocketSimulatorConfigData
  chatWebsocket: ChatWebSocketSimulatorConfigData
}

function getStringValue(
  metadata: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = metadata[key]
  return typeof value === "string" ? value : undefined
}

function getJsonStringValue(
  metadata: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = metadata[key]
  if (typeof value === "string") {
    return value
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return JSON.stringify(value, null, 2)
  }
  return undefined
}

function getNumberValue(
  metadata: Record<string, unknown>,
  key: string,
): number | undefined {
  const value = metadata[key]
  if (typeof value === "number") {
    return value
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function getBooleanValue(
  metadata: Record<string, unknown>,
  key: string,
): boolean | undefined {
  const value = metadata[key]
  return typeof value === "boolean" ? value : undefined
}

function getLiveKitDispatchModeValue(
  metadata: Record<string, unknown>,
): "automatic" | "explicit" | undefined {
  const value = metadata.livekit_dispatch_mode
  return value === "automatic" || value === "explicit" ? value : undefined
}

function getStringArrayValue(
  metadata: Record<string, unknown>,
  key: string,
): string[] | undefined {
  const value = metadata[key]
  return Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : undefined
}

export function createAgentPreviewMetadataView(
  metadata: Record<string, unknown>,
): AgentPreviewMetadataView {
  return {
    voice: {
      phoneNumber: getSimulatorMetadataString(metadata, "phone_number"),
      phoneNumberIndex: getPhoneNumberIndex(metadata),
      audioCodec: getStringValue(metadata, "audio_codec"),
      customSipHeaders:
        getJsonStringValue(metadata, "custom_sip_headers") ?? "",
      mediaEncryption: getStringValue(metadata, "media_encryption"),
    },
    outboundCall: {
      trigger_call_endpoint:
        getStringValue(metadata, "trigger_call_endpoint") ?? "",
      trigger_call_headers:
        getJsonStringValue(metadata, "trigger_call_headers") ?? "",
      trigger_call_payload:
        getJsonStringValue(metadata, "trigger_call_payload") ?? "",
      phone_number_key: getStringValue(metadata, "phone_number_key"),
    },
    pipecat: {
      apiKey: getStringValue(metadata, "pipecat_api_key") ?? "",
      agentName: getStringValue(metadata, "agent_name") ?? "",
      customData: getStringValue(metadata, "custom_data") ?? "",
    },
    livekit: {
      tokenEndpoint: getStringValue(metadata, "generate_token_endpoint") ?? "",
      livekitUrl: getStringValue(metadata, "livekit_url") ?? "",
      agentName: getStringValue(metadata, "livekit_agent_name") ?? "",
      dispatchMode: getLiveKitDispatchModeValue(metadata),
    },
    stt: {
      provider: getSimulatorMetadataString(metadata, "provider", "openai"),
      model: getSimulatorMetadataString(metadata, "model"),
      language: getStringValue(metadata, "language") ?? "en",
    },
    websocket: {
      endpoint: getStringValue(metadata, "endpoint"),
      initialization_json: getStringValue(metadata, "initialization_json"),
      authorization_header: getStringValue(metadata, "authorization_header"),
      custom_headers: getJsonStringValue(metadata, "custom_headers"),
      send_sample_rate_hertz: getNumberValue(
        metadata,
        "send_sample_rate_hertz",
      ),
      receive_sample_rate_hertz: getNumberValue(
        metadata,
        "receive_sample_rate_hertz",
      ),
      handshake_ready_message_type: getStringValue(
        metadata,
        "handshake_ready_message_type",
      ),
      handshake_requires_session_id: getBooleanValue(
        metadata,
        "handshake_requires_session_id",
      ),
      handshake_timeout_seconds: getNumberValue(
        metadata,
        "handshake_timeout_seconds",
      ),
      send_audio_template: getStringValue(metadata, "send_audio_template"),
      send_media_template: getStringValue(metadata, "send_media_template"),
      send_dtmf_template: getStringValue(metadata, "send_dtmf_template"),
      message_type_path: getStringValue(metadata, "message_type_path"),
      audio_message_type_value: getStringValue(
        metadata,
        "audio_message_type_value",
      ),
      audio_data_path: getStringValue(metadata, "audio_data_path"),
      audio_encoding: getStringValue(metadata, "audio_encoding"),
      receive_audio_channels: getNumberValue(
        metadata,
        "receive_audio_channels",
      ),
      non_audio_event_message_types: getStringArrayValue(
        metadata,
        "non_audio_event_message_types",
      ),
      websocket_compat_profile: getStringValue(
        metadata,
        "websocket_compat_profile",
      ),
      connection_mode: getStringValue(metadata, "connection_mode"),
      http_url: getStringValue(metadata, "http_url"),
      http_method: getStringValue(metadata, "http_method"),
      http_request_body: getStringValue(metadata, "http_request_body"),
      http_headers: getStringValue(metadata, "http_headers"),
      websocket_url_response_path: getStringValue(
        metadata,
        "websocket_url_response_path",
      ),
      marks_enabled: getBooleanValue(metadata, "marks_enabled"),
      speech_activity_enabled: getBooleanValue(
        metadata,
        "speech_activity_enabled",
      ),
      voice_url: getStringValue(metadata, "voice_url"),
      voice_http_method: getStringValue(metadata, "voice_http_method"),
      voice_http_headers: getStringValue(metadata, "voice_http_headers"),
      voice_form_fields: getStringValue(metadata, "voice_form_fields"),
      twilio_account_sid: getStringValue(metadata, "twilio_account_sid"),
      twilio_from_number: getStringValue(metadata, "twilio_from_number"),
      twilio_to_number: getStringValue(metadata, "twilio_to_number"),
    },
    chatWebsocket: {
      endpoint: getStringValue(metadata, "endpoint"),
      connection_mode: getStringValue(metadata, "connection_mode"),
      initialization_json: getStringValue(metadata, "initialization_json"),
      authorization_header: getStringValue(metadata, "authorization_header"),
      custom_headers: getJsonStringValue(metadata, "custom_headers"),
      http_url: getStringValue(metadata, "http_url"),
      http_method: getStringValue(metadata, "http_method"),
      http_request_body: getStringValue(metadata, "http_request_body"),
      http_headers: getStringValue(metadata, "http_headers"),
      websocket_url_response_path: getStringValue(
        metadata,
        "websocket_url_response_path",
      ),
      send_message_template: getStringValue(metadata, "send_message_template"),
      receive_message_text_path: getStringValue(
        metadata,
        "receive_message_text_path",
      ),
      receive_message_type_path: getStringValue(
        metadata,
        "receive_message_type_path",
      ),
      text_message_type_values: getStringArrayValue(
        metadata,
        "text_message_type_values",
      ),
      agent_disconnect_message_type: getStringValue(
        metadata,
        "agent_disconnect_message_type",
      ),
      receive_message_direction_path: getStringValue(
        metadata,
        "receive_message_direction_path",
      ),
      outbound_direction_value: getStringValue(
        metadata,
        "outbound_direction_value",
      ),
      handshake_ready_message_type: getStringValue(
        metadata,
        "handshake_ready_message_type",
      ),
      handshake_timeout_seconds: getNumberValue(
        metadata,
        "handshake_timeout_seconds",
      ),
      message_coalesce_timeout_seconds: getNumberValue(
        metadata,
        "message_coalesce_timeout_seconds",
      ),
      initial_message: getStringValue(metadata, "initial_message"),
    },
  }
}
